/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Card from "@mui/material/Card";
import { Checkbox, FormControlLabel, CircularProgress } from "@mui/material";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";

// Custom components
import ApnaTabs from "components/ApnaTabs/ApnaTabs";
import DeleteDialog from "components/DeleteDialog/DeleteDialog";
import PaginationBar from "components/PaginationBar/PaginationBar";

// Services
import { getAllTutorPaymentCycles } from "services/financeService";

// React imports
import { useState, useEffect } from "react";

// Table data builder
import { buildTutorPayrollTableData } from "Views/Activities/TutorsPayroll/data/tutorsPayrollTableData";

function TutorPayroll() {
  // State management
  const [payrollData, setPayrollData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDeleteDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [statusFilter, setStatusFilter] = useState("All");
  const [dueClassFilter, setDueClassFilter] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    limit: 10,
  });
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Tab counts state - all tabs in one object
  const [tabCounts, setTabCounts] = useState({
    All: 0,
    Hold: 0,
    Active: 0,
    Completed: 0,
    Discontinued: 0,
    SME: 0,
    Combined: 0,
    Separate: 0,
  });

  // Fetch payroll data
  const fetchPayrollData = async (page = 1, filter = null, limit = rowsPerPage) => {
    setLoading(true);
    try {
      const statusValues = ["Hold", "Active", "Completed", "Discontinued"];
      const tutorTypeValues = ["SME", "Combined", "Separate"];

      const params = {
        page: 1,
        limit: 100, // Backend max limit
      };

      // Store frontend filter type
      let frontendFilter = null;

      // Add filter based on active tab
      if (filter && filter !== "All") {
        if (statusValues.includes(filter)) {
          // It's a status filter - map frontend names to backend status
          const statusMap = {
            Hold: "PENDING",
            Active: "APPROVED",
            Completed: "PAID",
            Discontinued: "FAILED",
          };
          params.status = statusMap[filter];
        } else if (tutorTypeValues.includes(filter)) {
          // For SME/Combined/Separate, don't add status filter
          // We'll filter ALL data on frontend
          frontendFilter = filter;
        }
      }

      // Fetch first page to get total count
      const firstResponse = await getAllTutorPaymentCycles(params);

      if (firstResponse.success) {
        let allData = [...(firstResponse.data || [])];
        const totalPages = firstResponse.pagination?.totalPages || 1;

        // Fetch remaining pages if needed
        if (totalPages > 1) {
          const fetchPromises = [];
          for (let p = 2; p <= totalPages; p++) {
            fetchPromises.push(getAllTutorPaymentCycles({ ...params, page: p }));
          }
          const responses = await Promise.all(fetchPromises);
          responses.forEach((response) => {
            if (response.success) {
              allData = [...allData, ...(response.data || [])];
            }
          });
        }

        // Group data by tutorId to show each tutor only once
        const groupedData = {};
        const latestPaidDateByTutor = {};
        const allStudentsByTutor = {};

        allData.forEach((cycle) => {
          const tutorId = cycle.tutorId?._id;
          if (tutorId) {
            // Collect all unique students for this tutor
            if (!allStudentsByTutor[tutorId]) {
              allStudentsByTutor[tutorId] = [];
            }
            if (cycle.financeId?.students && cycle.financeId.students.length > 0) {
              cycle.financeId.students.forEach((student) => {
                // Check if student already exists in the list to avoid duplicates
                const exists = allStudentsByTutor[tutorId].some(
                  (existingStudent) =>
                    existingStudent._id === student._id || existingStudent.name === student.name
                );
                if (!exists) {
                  allStudentsByTutor[tutorId].push(student);
                }
              });
            }

            if (cycle.status === "PAID" && (cycle.paymentDate || cycle.paidAt)) {
              const paidDate = cycle.paymentDate || cycle.paidAt;
              const existingPaidDate = latestPaidDateByTutor[tutorId];
              if (!existingPaidDate || new Date(paidDate) > new Date(existingPaidDate)) {
                latestPaidDateByTutor[tutorId] = paidDate;
              }
            }

            // Prefer currently active/running cycle, otherwise most recent
            if (!groupedData[tutorId]) {
              groupedData[tutorId] = cycle;
            } else {
              const existingCycle = groupedData[tutorId];
              const isActiveStatus = ["ACTIVE", "PENDING", "APPROVED", "PROCESSING"].includes(
                cycle.status
              );
              const existingIsActive = ["ACTIVE", "PENDING", "APPROVED", "PROCESSING"].includes(
                existingCycle.status
              );

              // Priority: Active status > Recent date
              if (isActiveStatus && !existingIsActive) {
                groupedData[tutorId] = cycle;
              } else if (
                isActiveStatus === existingIsActive &&
                new Date(cycle.createdAt) > new Date(existingCycle.createdAt)
              ) {
                groupedData[tutorId] = cycle;
              }
            }
          }
        });

        // Add collected unique students to the tutor cycle
        let uniqueTutors = Object.values(groupedData).map((cycle) => ({
          ...cycle,
          allStudents: allStudentsByTutor[cycle.tutorId?._id] || [],
          lastPaymentDate: latestPaidDateByTutor[cycle.tutorId?._id] || cycle.paymentDate || null,
        }));

        console.log("Before frontend filter:", uniqueTutors.length, "Filter:", frontendFilter);

        // Apply frontend filtering for SME/Combined/Separate
        if (frontendFilter) {
          if (frontendFilter === "Combined") {
            // Combined = multiple students in same class
            uniqueTutors = uniqueTutors.filter((cycle) => {
              const studentsCount = cycle.financeId?.students?.length || 0;
              console.log("Combined check - Students count:", studentsCount);
              return studentsCount > 1;
            });
          } else if (frontendFilter === "Separate") {
            // Separate = single student
            uniqueTutors = uniqueTutors.filter((cycle) => {
              const studentsCount = cycle.financeId?.students?.length || 0;
              console.log("Separate check - Students count:", studentsCount);
              return studentsCount === 1;
            });
          } else if (frontendFilter === "SME") {
            // SME = Subject Matter Expert - check tutor's fields
            uniqueTutors = uniqueTutors.filter((cycle) => {
              const tutor = cycle.tutorId;
              const isSME =
                tutor?.isSME === true ||
                tutor?.tutorType === "SME" ||
                tutor?.type === "SME" ||
                tutor?.expertise === "SME";
              console.log("SME check - Tutor:", tutor?.fullName, "isSME:", isSME);
              return isSME;
            });
          }
        }

        console.log("After frontend filter:", uniqueTutors.length);

        // Apply pagination on grouped data
        const start = (page - 1) * limit;
        const end = start + limit;
        const paginatedData = uniqueTutors.slice(start, end);

        setPayrollData(paginatedData);
        setPagination({
          currentPage: page,
          totalPages: Math.ceil(uniqueTutors.length / limit),
          totalRecords: uniqueTutors.length,
          limit: limit,
        });
      }
    } catch (error) {
      console.error("Error fetching payroll data:", error);
      setPayrollData([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch counts for all tabs
  const fetchTabCounts = async () => {
    try {
      const counts = {};

      // Helper function to group by tutorId
      const groupByTutor = (data) => {
        const groupedData = {};
        data.forEach((cycle) => {
          const tutorId = cycle.tutorId?._id;
          if (tutorId) {
            if (!groupedData[tutorId]) {
              groupedData[tutorId] = cycle;
            } else {
              // Keep the most recent cycle
              const existingCycle = groupedData[tutorId];
              if (new Date(cycle.createdAt) > new Date(existingCycle.createdAt)) {
                groupedData[tutorId] = cycle;
              }
            }
          }
        });
        return Object.values(groupedData);
      };

      // Fetch all data first to count properly
      const firstResponse = await getAllTutorPaymentCycles({ page: 1, limit: 100 });
      let allData = [...(firstResponse.data || [])];
      const totalPages = firstResponse.pagination?.totalPages || 1;

      // Fetch remaining pages if needed
      if (totalPages > 1) {
        const fetchPromises = [];
        for (let p = 2; p <= totalPages; p++) {
          fetchPromises.push(getAllTutorPaymentCycles({ page: p, limit: 100 }));
        }
        const responses = await Promise.all(fetchPromises);
        responses.forEach((response) => {
          if (response.success) {
            allData = [...allData, ...(response.data || [])];
          }
        });
      }

      // Group all data by tutor
      const uniqueTutorData = groupByTutor(allData);

      // Count All unique tutors
      counts.All = uniqueTutorData.length;

      // Count by status
      counts.Hold = uniqueTutorData.filter((cycle) => cycle.status === "PENDING").length;
      counts.Active = uniqueTutorData.filter((cycle) => cycle.status === "APPROVED").length;
      counts.Completed = uniqueTutorData.filter((cycle) => cycle.status === "PAID").length;
      counts.Discontinued = uniqueTutorData.filter((cycle) => cycle.status === "FAILED").length;

      // Count Combined (multiple students)
      counts.Combined = uniqueTutorData.filter(
        (cycle) => cycle.financeId?.students && cycle.financeId.students.length > 1
      ).length;

      // Count Separate (single student)
      counts.Separate = uniqueTutorData.filter(
        (cycle) => cycle.financeId?.students && cycle.financeId.students.length === 1
      ).length;

      // Count SME tutors - check multiple possible fields
      counts.SME = uniqueTutorData.filter((cycle) => {
        const tutor = cycle.tutorId;
        return (
          tutor?.isSME === true ||
          tutor?.tutorType === "SME" ||
          tutor?.type === "SME" ||
          tutor?.expertise === "SME"
        );
      }).length;

      console.log("Fetched tab counts:", counts);
      console.log("Total unique tutors:", uniqueTutorData.length);
      console.log(
        "Sample cycle statuses:",
        uniqueTutorData.slice(0, 3).map((c) => c.status)
      );
      setTabCounts(counts);
    } catch (error) {
      console.error("Error fetching tab counts:", error);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchPayrollData(1, statusFilter);
    fetchTabCounts();
  }, []);

  // Fetch data when status filter changes
  useEffect(() => {
    fetchPayrollData(1, statusFilter);
    // Also refresh counts when tab changes
    fetchTabCounts();
  }, [statusFilter]);

  // Handle delete
  const handleDeleteClick = (item) => {
    setSelectedItem(item);
    setDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    // Implement delete logic here
    console.log("Delete item:", selectedItem);
    setDeleteDialog(false);
    setSelectedItem(null);
    // Refresh data after delete
    fetchPayrollData(pagination.currentPage, statusFilter);
  };

  const handleClose = () => {
    setDeleteDialog(false);
    setSelectedItem(null);
  };

  // Handle tab change
  const handleTabChange = (tabLabel) => {
    setStatusFilter(tabLabel);
  };

  // Handle page change
  const handlePageChange = (event, newPage) => {
    fetchPayrollData(newPage, statusFilter, rowsPerPage);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    fetchPayrollData(1, statusFilter, newRowsPerPage);
  };

  // Handle due class filter
  const handleDueClassChange = (event) => {
    setDueClassFilter(event.target.checked);
    // Implement due class filter logic if needed
  };

  // Build table data
  const { columns, rows } = buildTutorPayrollTableData(payrollData, handleDeleteClick);

  // Left tabs - Payment Status
  const leftTabsData = [
    { label: "All", count: tabCounts.All, color: "#2027e1ff", content: "", status: "All" },
    { label: "Hold", count: tabCounts.Hold, color: "#feca57", content: "", status: "Hold" },
    { label: "Active", count: tabCounts.Active, color: "#10ec3cff", content: "", status: "Active" },
    {
      label: "Completed",
      count: tabCounts.Completed,
      color: "#10a6ecff",
      content: "",
      status: "Completed",
    },
    {
      label: "Discontinued",
      count: tabCounts.Discontinued,
      color: "#ff0000",
      content: "",
      status: "Discontinued",
    },
  ];

  // Right tabs - Tutor Type
  const rightTabsData = [
    { label: "SME", count: tabCounts.SME, color: "#ff9800", content: "", status: "SME" },
    {
      label: "Combined",
      count: tabCounts.Combined,
      color: "#9c27b0",
      content: "",
      status: "Combined",
    },
    {
      label: "Separate",
      count: tabCounts.Separate,
      color: "#00bcd4",
      content: "",
      status: "Separate",
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonTypography mb={2} variant="h6" fontWeight="medium">
                Tutor Payroll
              </ArgonTypography>

              {/* Tabs Section - Left and Right */}
              <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                {/* Left Tabs - Payment Status */}
                <ArgonBox>
                  <ApnaTabs
                    tabsData={leftTabsData}
                    onTabChange={handleTabChange}
                    activeTab={statusFilter}
                  />
                </ArgonBox>

                {/* Right Tabs - Cycle Type */}
                <ArgonBox>
                  <ApnaTabs
                    tabsData={rightTabsData}
                    onTabChange={handleTabChange}
                    activeTab={statusFilter}
                  />
                </ArgonBox>
              </ArgonBox>
            </ArgonBox>

            {loading ? (
              <ArgonBox display="flex" justifyContent="center" alignItems="center" p={5}>
                <CircularProgress />
              </ArgonBox>
            ) : payrollData.length === 0 ? (
              <ArgonBox display="flex" justifyContent="center" alignItems="center" p={5}>
                <ArgonTypography variant="h6" color="text" fontWeight="medium">
                  No data found for this tab
                </ArgonTypography>
              </ArgonBox>
            ) : (
              <>
                <ArgonBox
                  sx={{
                    "& .MuiTableRow-root:not(:last-child)": {
                      "& td": {
                        borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                          `${borderWidth[1]} solid ${borderColor}`,
                      },
                    },
                  }}
                >
                  <Table columns={columns} rows={rows} />
                </ArgonBox>

                {/* Pagination */}
                {payrollData.length > 0 && (
                  <PaginationBar
                    currentPage={pagination.currentPage}
                    rowsPerPage={rowsPerPage}
                    totalItems={pagination.totalRecords}
                    totalPages={pagination.totalPages}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                  />
                )}
              </>
            )}
          </Card>
        </ArgonBox>

        <DeleteDialog
          open={dialog}
          onClose={handleClose}
          onConfirm={handleDeleteConfirm}
          title="Delete Tutor Payroll"
          description="Are you sure you want to delete this tutor payroll record?"
          cancelText="Cancel"
          confirmText="Confirm"
        />
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default TutorPayroll;
